/**
 * Fornecedores — quem vende PARA a loja.
 *
 * Mesma forma de `TelaClientes.tsx` (listagem, cadastro e edição no mesmo
 * lugar) e o mesmo cuidado com a honestidade do dado: o protótipo
 * (`Lumma Fit Fornecedores.dc.html`) mostra "Compras em 12 meses", "A pagar em
 * aberto" e "Último pedido" — os três dependem do módulo de compra, que ainda
 * não existe. A API declara isso no bloco `compras`, e esta tela mostra o
 * texto de espera em vez de desenhar "R$ 0,00", que seria uma afirmação falsa.
 *
 * Sem crediário, sem preferência de grade e sem os dois consentimentos de
 * marketing: fornecedor não é titular de dado pessoal de consumo, e a loja não
 * manda campanha para quem vende a ela.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { BotaoExportar } from '../../componentes/BotaoExportar'
import type { ColunaExportacao } from '../../componentes/exportacao'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import {
  carregarFichaFornecedor,
  carregarFornecedores,
  type FichaFornecedor,
  type Filtro,
  type KPI,
  type LinhaFornecedor,
  type ListaDeFornecedores,
} from '../../plataforma/fornecedores'
import { mascararDocumento, mascararTelefone } from '../../plataforma/mascaras'
import { LayoutApp } from '../app/LayoutApp'

/** Mesma ideia de `TelaClientes.tsx`: a leitura da tabela, em texto puro. */
const COLUNAS_EXPORTACAO_FORNECEDORES: ColunaExportacao<LinhaFornecedor>[] = [
  { cabecalho: 'Código', valor: (f) => f.codigo },
  { cabecalho: 'Fornecedor', valor: (f) => f.nome },
  {
    cabecalho: 'Documento',
    valor: (f) =>
      f.documento
        ? `${f.tipo === 'pj' ? 'CNPJ' : 'CPF'} ${mascararDocumento(f.tipo, f.documento)}`
        : f.tipo_rotulo,
  },
  { cabecalho: 'Telefone', valor: (f) => (f.telefone ? mascararTelefone(f.telefone) : '') },
  { cabecalho: 'E-mail', valor: (f) => f.email ?? '' },
  {
    cabecalho: 'Condições',
    valor: (f) => (f.prazo_pagamento_dias !== undefined ? `${f.prazo_pagamento_dias} dias` : 'A combinar'),
  },
  { cabecalho: 'Situação', valor: (f) => f.status_rotulo },
]

const ICONE_KPI: Record<string, NomeIcone> = {
  cadastrados: 'store',
  ativos: 'check_circle',
  com_prazo: 'payments',
  compras_12m: 'shopping_cart',
}

export function TelaFornecedores() {
  const navegar = useNavigate()

  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [lista, setLista] = useState<ListaDeFornecedores | null>(null)
  const [erro, setErro] = useState('')
  // Muda a cada gravação, para a listagem recarregar sem recriar o estado.
  const [versao, setVersao] = useState(0)

  const [aberta, setAberta] = useState<FichaFornecedor | null>(null)

  // Debounce da busca: cada tecla disparando uma consulta que varre a base é
  // como se derruba o próprio banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()

    carregarFornecedores(buscaAtiva, filtro, controle.signal)
      .then((dados) => {
        setLista(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os fornecedores.')
      })

    return () => controle.abort()
  }, [buscaAtiva, filtro, versao])

  const abrirFicha = useCallback(async (codigo: string) => {
    try {
      setAberta(await carregarFichaFornecedor(codigo))
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir a ficha.')
    }
  }, [])

  // Editar direto da lista pula a gaveta. Agora é navegação de verdade, e não
  // uma troca de `useState`: a edição tem endereço próprio, então o Voltar do
  // navegador funciona e o F5 no meio do formulário não descarta tudo.
  const editarDireto = useCallback(
    (codigo: string) => navegar(`/app/fornecedores/${encodeURIComponent(codigo)}/editar`),
    [navegar],
  )

  const subtitulo = lista
    ? `${lista.base} ${lista.base === 1 ? 'fornecedor cadastrado' : 'fornecedores cadastrados'}`
    : 'Carregando…'

  const rotuloDoFiltro = lista?.filtros.find((chip) => chip.chave === filtro)?.rotulo
  const filtroAplicado = [
    buscaAtiva && `Busca: "${buscaAtiva}"`,
    filtro !== 'todos' && rotuloDoFiltro && `Filtro: ${rotuloDoFiltro}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <LayoutApp
      titulo="Fornecedores"
      subtitulo={subtitulo}
      acoes={
        <>
          {lista && (
            <BotaoExportar
              nomeBase="fornecedores"
              titulo="Fornecedores"
              filtroAplicado={filtroAplicado || undefined}
              colunas={COLUNAS_EXPORTACAO_FORNECEDORES}
              linhas={lista.linhas}
            />
          )}
          <LinkBotao para="/app/fornecedores/novo" icone="store">
            Novo fornecedor
          </LinkBotao>
        </>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {erro && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
          >
            <span className="min-w-0 flex-1">{erro}</span>
            <button
              type="button"
              onClick={() => setVersao((n) => n + 1)}
              className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
            >
              Tentar de novo
            </button>
          </div>
        )}

        {!lista ? (
          <Esqueleto />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {lista.kpis.map((kpi) => (
                <CartaoKPI key={kpi.chave} kpi={kpi} />
              ))}
            </div>

            <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
              <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
                {/* `basis-[240px]` + `min-w-0`: regra 3 do "layout que já
                    custou bug" — input dentro de composto precisa dos dois, ou
                    estica o flex e empurra os chips para fora. */}
                <label className="composto h-[34px] min-w-0 flex-1 basis-[240px] cursor-text">
                  <Icone nome="search" tamanho={18} className="text-texto-fraco" />
                  <input
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                    placeholder="Buscar por razão social, CNPJ ou telefone"
                  />
                  <span className="sr-only">Buscar fornecedor</span>
                </label>

                {lista.filtros.map((chip) => {
                  const ativo = chip.chave === filtro
                  return (
                    <button
                      key={chip.chave}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => setFiltro(chip.chave)}
                      className={`flex h-[34px] items-center gap-2 rounded-sistema border px-3.5 text-[13.5px] font-semibold ${
                        ativo
                          ? 'border-marca bg-marca-suave text-marca-forte'
                          : 'border-borda bg-superficie text-tinta'
                      }`}
                    >
                      {chip.rotulo}
                      <span
                        className={`tabular rounded-full px-[7px] py-[2px] text-[11px] font-bold ${
                          ativo ? 'bg-marca-barra text-marca-forte' : 'bg-divisoria text-texto-suave'
                        }`}
                      >
                        {chip.total}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/*
                A honestidade da tabela, dita uma vez. O protótipo tem as
                colunas "Último pedido" e "Compras 12m"; as duas dependem de
                compra, e a API declara isso em `compras.fonte`.
              */}
              {lista.compras.fonte === 'aguarda_compra' && lista.linhas.length > 0 && (
                <div className="flex gap-2.5 border-b border-marca-barra bg-marca-fundo-2 px-4 py-2.5">
                  <Icone nome="info" tamanho={18} className="text-marca" />
                  <p className="text-[12.5px] leading-[1.5] text-marca-forte">
                    Último pedido e compras em 12 meses aparecem quando o módulo de Compras existir.{' '}
                    {lista.compras.espera}
                  </p>
                </div>
              )}

              {lista.linhas.length === 0 ? (
                <EstadoVazio
                  base={lista.base}
                  busca={lista.busca ?? ''}
                  filtro={filtro}
                  aoCadastrar={() => navegar('/app/fornecedores/novo')}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[820px] border-collapse">
                    <caption className="sr-only">Fornecedores cadastrados na loja</caption>
                    <thead>
                      <tr className="bg-fundo">
                        {['Código', 'Fornecedor', 'Contato', 'Condições', 'Situação', ''].map(
                          (titulo, i) => (
                            <th
                              key={titulo || 'acoes'}
                              scope="col"
                              className={`border-b border-borda py-2.5 text-left text-[12.5px] font-semibold text-texto-suave ${
                                i === 0 ? 'pr-3 pl-4' : 'px-3'
                              }`}
                            >
                              {titulo}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {lista.linhas.map((linha, i) => (
                        <LinhaDaTabela
                          key={linha.codigo}
                          linha={linha}
                          ultima={i === lista.linhas.length - 1}
                          aoAbrir={() => void abrirFicha(linha.codigo)}
                          aoEditar={() => void editarDireto(linha.codigo)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-borda px-4 py-2.5">
                <span className="tabular text-[13px] text-texto-suave">
                  {lista.total} de {lista.base}{' '}
                  {lista.base === 1 ? 'fornecedor' : 'fornecedores'}
                </span>
                {lista.total < lista.base && (
                  <span className="text-[12.5px] text-texto-fraco">
                    Refine a busca para ver o resto da base.
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {aberta && <Drawer ficha={aberta} aoFechar={() => setAberta(null)} />}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ KPI */

function CartaoKPI({ kpi }: { kpi: KPI }) {
  const esperando = kpi.fonte !== 'real'
  const valor = kpi.formato === 'dinheiro' ? formatarCentavos(kpi.valor) : String(kpi.valor)

  return (
    <div className="rounded-sistema border border-borda bg-superficie px-4 py-3.5 shadow-[var(--shadow-cartao)]">
      <div className="mb-2.5 flex items-center gap-2 text-[12.5px] text-texto-suave">
        <Icone nome={ICONE_KPI[kpi.chave] ?? 'store'} tamanho={18} className="text-texto-fraco" />
        {kpi.rotulo}
      </div>
      {/*
        Bloco que aguarda compra NÃO mostra o número. Um "R$ 0,00" cinza ainda
        é um valor na tela, e quem olha de longe lê como medição.
      */}
      {esperando ? (
        <div className="text-[15px] font-semibold text-texto-fraco">—</div>
      ) : (
        <div className="tabular text-[21px] font-bold text-tinta">{valor}</div>
      )}
      <div className="mt-1 text-xs text-texto-suave">{kpi.espera ?? kpi.detalhe ?? ''}</div>
    </div>
  )
}

/** Formata centavos com símbolo, sem depender de `dinheiro.ts` para um único
 *  uso: o KPI de "compras 12m" hoje só existe como zero em espera. */
function formatarCentavos(centavos: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    centavos / 100,
  )
}

/* --------------------------------------------------------------- tabela */

function LinhaDaTabela({
  linha,
  ultima,
  aoAbrir,
  aoEditar,
}: {
  linha: LinhaFornecedor
  ultima: boolean
  aoAbrir: () => void
  aoEditar: () => void
}) {
  const borda = ultima ? '' : 'border-b border-divisoria'

  return (
    <tr>
      {/*
        O código é a primeira coluna, e é `tabular`: é o que o balcão dita ao
        telefone e o que a pessoa procura correndo o olho pela lista.
      */}
      <td className={`tabular py-2.5 pr-3 pl-4 text-[13px] font-semibold text-texto-suave ${borda}`}>
        {linha.codigo}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <button
          type="button"
          onClick={aoAbrir}
          className="text-left text-[13.5px] font-semibold text-tinta hover:text-marca"
        >
          {linha.nome}
        </button>
        <div className="tabular mt-0.5 text-xs text-texto-suave">
          {linha.documento
            ? `${linha.tipo === 'pj' ? 'CNPJ' : 'CPF'} ${mascararDocumento(linha.tipo, linha.documento)}`
            : linha.tipo_rotulo}
        </div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <div className="tabular text-[13px] text-tinta">
          {linha.telefone ? mascararTelefone(linha.telefone) : '—'}
        </div>
        {linha.email && (
          <div className="mt-0.5 max-w-[180px] truncate text-xs text-texto-suave">
            {linha.email}
          </div>
        )}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <div className="tabular text-[13px] text-tinta">
          {linha.prazo_pagamento_dias !== undefined
            ? `${linha.prazo_pagamento_dias} dias`
            : 'A combinar'}
        </div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <span
          className={`inline-flex items-center rounded-[6px] border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
            linha.status === 'ativo'
              ? 'border-ok-suave bg-ok-suave text-ok-texto'
              : 'border-borda bg-superficie text-texto-suave'
          }`}
        >
          {linha.status_rotulo}
        </span>
      </td>

      <td className={`px-3 py-2.5 text-right ${borda}`}>
        <MenuAcoesLinha nome={linha.nome} aoAbrir={aoAbrir} aoEditar={aoEditar} />
      </td>
    </tr>
  )
}

/** Mesmo componente de `TelaClientes.tsx` — ver o comentário lá para o porquê do portal. */
function MenuAcoesLinha({
  nome,
  aoAbrir,
  aoEditar,
}: {
  nome: string
  aoAbrir: () => void
  aoEditar: () => void
}) {
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState({ top: 0, right: 0 })
  const botaoRef = useRef<HTMLButtonElement>(null)

  const abrir = () => {
    const retangulo = botaoRef.current?.getBoundingClientRect()
    if (retangulo) {
      setPosicao({ top: retangulo.bottom + 4, right: window.innerWidth - retangulo.right })
    }
    setAberto(true)
  }

  useEffect(() => {
    if (!aberto) return
    const fechar = () => setAberto(false)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    return () => {
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
    }
  }, [aberto])

  return (
    <>
      <button
        ref={botaoRef}
        type="button"
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-expanded={aberto}
        aria-haspopup="menu"
        className="flex h-8 w-8 items-center justify-center rounded-sistema text-texto-suave hover:bg-divisoria hover:text-tinta"
      >
        <Icone nome="more_vert" tamanho={19} />
        <span className="sr-only">Ações de {nome}</span>
      </button>

      {aberto &&
        createPortal(
          <>
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setAberto(false)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              role="menu"
              style={{ top: posicao.top, right: posicao.right }}
              className="fixed z-50 w-[170px] rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false)
                  aoAbrir()
                }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] font-medium text-texto hover:bg-divisoria"
              >
                <Icone nome="visibility" tamanho={17} />
                Ver ficha
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false)
                  aoEditar()
                }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] font-medium text-texto hover:bg-divisoria"
              >
                <Icone nome="edit_note" tamanho={17} />
                Editar fornecedor
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  )
}

/* ---------------------------------------------------------- estado vazio */

function EstadoVazio({
  base,
  busca,
  filtro,
  aoCadastrar,
}: {
  base: number
  busca: string
  filtro: Filtro
  aoCadastrar: () => void
}) {
  // Vazio INICIAL é diferente de vazio por filtro: o primeiro explica o que a
  // tela faz e oferece a ação; o segundo manda ajustar a busca.
  const inicial = base === 0 && busca === '' && filtro === 'todos'

  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <Icone
        nome={inicial ? 'store' : 'person_search'}
        tamanho={26}
        className="text-inativo"
      />
      <p className="text-sm font-semibold text-tinta">
        {inicial ? 'Nenhum fornecedor cadastrado' : 'Nenhum fornecedor encontrado'}
      </p>
      <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
        {inicial
          ? 'Cadastre quem vende para a loja para guardar contato, condições de pagamento e endereço — e para a compra achar o fornecedor na hora do pedido.'
          : 'Ajuste a busca ou remova os filtros ativos.'}
      </p>
      {inicial && (
        <div className="mt-2">
          <Botao icone="store" onClick={aoCadastrar}>
            Cadastrar o primeiro fornecedor
          </Botao>
        </div>
      )}
    </div>
  )
}

/** Skeleton de linha, nunca spinner de página inteira (spec 03). */
function Esqueleto() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[104px] animate-pulse rounded-sistema bg-superficie" />
        ))}
      </div>
      <div className="h-[280px] animate-pulse rounded-sistema bg-superficie" />
    </div>
  )
}

/* --------------------------------------------------------------- drawer */

function Drawer({ ficha, aoFechar }: { ficha: FichaFornecedor; aoFechar: () => void }) {
  return (
    <Gaveta rotulo={`Ficha de ${ficha.nome}`} aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo={ficha.nome}
        subtitulo={`Ficha do fornecedor ${ficha.codigo}`}
        rotuloFechar="Fechar ficha"
        aoFechar={aoFechar}
      />

      <CorpoDaGaveta>
          <div className="mb-5">
            <div className="flex items-baseline gap-2.5">
              <span className="font-titulo text-[19px] font-semibold text-tinta">
                {ficha.nome}
              </span>
              {/* O código ao lado do nome, e não escondido no rodapé: é por ele
                  que a loja se refere ao fornecedor no telefone. */}
              <span className="tabular rounded-[6px] bg-divisoria px-2 py-[2px] text-[12px] font-bold text-texto-suave">
                {ficha.codigo}
              </span>
            </div>
            <div className="tabular mt-1 text-[13px] text-texto-suave">
              {ficha.documento
                ? `${ficha.tipo === 'pj' ? 'CNPJ' : 'CPF'} ${mascararDocumento(ficha.tipo, ficha.documento)}`
                : ficha.tipo_rotulo}
            </div>
            {ficha.nome_fantasia && (
              <div className="mt-1 text-[13px] text-texto-suave">{ficha.nome_fantasia}</div>
            )}
          </div>

          <Secao titulo="Contato">
            <LinhaFicha
              rotulo="Telefone"
              valor={ficha.contato.telefone ? mascararTelefone(ficha.contato.telefone) : '—'}
            />
            <LinhaFicha rotulo="E-mail" valor={ficha.contato.email ?? '—'} />
          </Secao>

          <Secao titulo="Endereço">
            {ficha.endereco.cep ? (
              <>
                <LinhaFicha
                  rotulo="Logradouro"
                  valor={[ficha.endereco.logradouro, ficha.endereco.numero]
                    .filter(Boolean)
                    .join(', ') || '—'}
                />
                <LinhaFicha rotulo="Bairro" valor={ficha.endereco.bairro ?? '—'} />
                <LinhaFicha
                  rotulo="Cidade"
                  valor={[ficha.endereco.cidade, ficha.endereco.uf].filter(Boolean).join(' / ') || '—'}
                />
                <LinhaFicha rotulo="CEP" valor={ficha.endereco.cep ?? '—'} />
              </>
            ) : (
              <p className="py-2 text-[13px] text-texto-suave">Sem endereço cadastrado.</p>
            )}
          </Secao>

          <Secao titulo="Condições comerciais">
            <LinhaFicha
              rotulo="Prazo de pagamento"
              valor={
                ficha.prazo_pagamento_dias !== undefined
                  ? `${ficha.prazo_pagamento_dias} dias`
                  : 'A combinar por pedido'
              }
            />
            {ficha.inscricao_estadual && (
              <LinhaFicha rotulo="Inscrição estadual" valor={ficha.inscricao_estadual} />
            )}
          </Secao>

          <Secao titulo="Últimos pedidos">
            {ficha.compras.linhas.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-sistema border border-dashed border-borda px-4 py-7 text-center">
                <Icone nome="receipt_long" tamanho={22} className="text-inativo" />
                <p className="max-w-[280px] text-[12.5px] leading-[1.5] text-texto-suave">
                  {ficha.compras.espera}
                </p>
              </div>
            ) : (
              ficha.compras.linhas.map((pedido) => (
                <div
                  key={pedido.quando + pedido.resumo}
                  className="flex gap-3 border-t border-divisoria py-2.5 first:border-t-0"
                >
                  <span className="tabular w-14 shrink-0 text-[12.5px] text-texto-suave">
                    {pedido.quando.slice(0, 10)}
                  </span>
                  <span className="min-w-0 flex-1 text-[13px] text-tinta">{pedido.resumo}</span>
                  <span className="tabular shrink-0 text-[13px] font-semibold text-tinta">
                    {formatarCentavos(pedido.total_centavos)}
                  </span>
                </div>
              ))
            )}
          </Secao>

          {ficha.observacao_interna && (
            <Secao titulo="Observação interna">
              <p className="text-[13px] leading-[1.6] text-texto">{ficha.observacao_interna}</p>
              <p className="mt-2 text-[12px] text-texto-fraco">
                Visível só para a equipe, nunca na nota.
              </p>
            </Secao>
          )}
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
        <LinkBotao
          para={`/app/fornecedores/${encodeURIComponent(ficha.codigo)}/editar`}
          icone="edit_note"
        >
          Editar fornecedor
        </LinkBotao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="mb-3 text-[11px] font-bold tracking-[0.6px] text-texto-fraco uppercase">
        {titulo}
      </h3>
      {children}
    </section>
  )
}

function LinhaFicha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-4 border-t border-divisoria py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular min-w-0 text-right font-semibold break-words text-tinta">
        {valor}
      </span>
    </div>
  )
}
